/**
 * EIN ANLASS, DEN DIE LISTE NICHT KENNT, WIRD TROTZDEM LESBAR.
 *
 *   ORIGINALTEXT MARVIN (08.09.2026, zur Glocke)
 *     „«Termin» wird groß geschrieben am anfang"
 *
 * Der Server liefert die Anlässe mit, die er BENENNEN kann: die Läufe aus
 * `automatik.name` und die festen aus `regeln/anlasstext.ts`. Was er nicht
 * benennen kann, ist kein Versehen, sondern die Bauart des Feldes -
 * `nachricht_log.anlass` trägt auch FREIEN TEXT:
 *
 *   `AAZ an Koblenz`      aus der Papierart und dem Namen der Gemeinde
 *   `von hand: <Anlass>`  aus dem, was jemand eingetippt hat
 *
 * DESHALB ZWEI REGELN UND NICHT EINE:
 *
 *   ein Wort ohne Leerzeichen ist ein SCHLÜSSEL. Unterstriche werden zu
 *   Leerzeichen - `kunden_nachfassen` liest sich sonst wie ein Bezeichner,
 *   und das ist er auf einem Bildschirm nicht.
 *
 *   etwas mit einem Leerzeichen darin hat schon ein Mensch geschrieben. Es
 *   bleibt WÖRTLICH stehen: wer aus „AAZ an Koblenz" ein „Aaz an koblenz"
 *   machte, änderte einen Ortsnamen.
 *
 * Der erste Buchstabe wird in beiden Fällen gross. Das ist Marvins Punkt,
 * und er gilt für jeden Anlass und nicht nur für den einen, den er gesehen
 * hat.
 *
 * REINE REGELN, OHNE JSX DANEBEN. Eine Datei mit JSX lässt sich mit
 * `--experimental-strip-types` nicht laden, und dann bliebe nur die
 * Textsuche - **eine Textsuche misst, dass etwas dasteht, nicht, dass es
 * stimmt.**
 */

/** Ein Anlass, wie ihn ein Mensch lesen soll. */
export function anlassLesbar(anlass: unknown): string {
  const roh = String(anlass ?? '').trim();

  /* EIN LEERER ANLASS IST KEINE LEERE ZEILE. Stünde dort nichts, sähe die
     Meldung aus, als fehlte ihr etwas - sie hat nur keinen Anlass. */
  if (!roh) return 'Ohne Anlass';

  /* Ein Schlüssel trägt kein Leerzeichen. Was eines trägt, hat ein Mensch
     geschrieben und wird nicht umgeschrieben. */
  let text = roh.includes(' ') ? roh : roh.replace(/_/g, ' ');
  /* UND DIE UMLAUTE ZURÜCK (.445). Ein Schlüssel trägt ae, oe, ue; auf dem
     Bildschirm steht Deutsch. Nur die Schreibweisen, die im Haus vorkommen,
     damit aus „Dauer" kein „Dauer" mit Umlaut wird. */
  text = text
    .replace(/\bbehoerden/gi, (m) => (m[0] === 'B' ? 'Behörden' : 'behörden'))
    .replace(/selbsttaetig/g, 'selbsttätig')
    .replace(/\bueberfaellig/g, 'überfällig')
    .replace(/faellig/g, 'fällig')
    .replace(/kapazitaet/g, 'kapazität')
    .replace(/\bgebuehr/g, 'gebühr')
    .replace(/pruef/g, 'prüf')
    .replace(/\bauftraege/g, 'aufträge')
    .replace(/\bwaechter/g, 'wächter')
    .replace(/\baufmass\b/g, 'aufmaß')
    .replace(/\bstrasse/g, 'straße');

  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Der Text zu einem Anlass: erst das, was der Server benennen konnte, sonst
 * die lesbare Fassung des Rohwerts.
 *
 * SIE STEHT HIER UND NICHT AN DER GLOCKE, weil sie zwei Quellen
 * zusammenlegt und genau das die Stelle ist, an der zwei Fassungen
 * entstehen. Wer eine zweite Liste danebenstellt, hat sie beim nächsten
 * Nachtrag.
 */
export function anlassText(
  anlass: unknown, bekannt?: Record<string, string> | null
): string {
  const roh = String(anlass ?? '').trim();
  const treffer = roh ? bekannt?.[roh] : null;
  return (treffer && treffer.trim()) ? treffer : anlassLesbar(roh);
}
